import { StateParams, StateService, UIRouterGlobals } from "@uirouter/angular";
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from "@angular/core";
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable, Subscription, take, takeUntil } from "rxjs";
import { IIndicatorMap } from "@gtmhub/error-handling";
import { ContributorTypeEnum } from "@gtmhub/goals/models";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { filterOpenedActiveAndFutureSessions } from "@gtmhub/sessions/redux/session-selectors";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { getCurrentUserId } from "@gtmhub/users";
import { hasPermissions } from "@gtmhub/users/redux";
import { Flavor, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { CfMap } from "@webapp/custom-fields/models/custom-fields.models";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { IParentSelector, ParentOkrSelectorType } from "@webapp/okrs/models/parent-selector.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import { Task, TaskStatus } from "@webapp/tasks/models/tasks.models";
import { TasksFacade } from "@webapp/tasks/services/task-facade.service";

type TaskParentItem = (Goal | Metric) & { type?: ParentOkrSelectorType };

interface CreateTaskForm {
  name: FormControl<string>;
  status: FormControl<string>;
  dueDate: FormControl<string>;
}

interface FieldsToPreventDefault {
  name: boolean;
  dueDate: boolean;
  linkedTo: boolean;
}

@Component({
  selector: "create-task",
  templateUrl: "./create-task.component.html",
  styleUrls: ["./create-task.component.less"],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTaskComponent implements OnInit {
  private fieldsToPreventDefault: FieldsToPreventDefault = {
    name: true,
    dueDate: true,
    linkedTo: true,
  };
  private parent: TaskParentItem;

  public form: FormGroup<CreateTaskForm>;
  public task: Task;
  public statuses = TaskStatus;
  public allOpenSessionsIds: string[];
  public indicators: IIndicatorMap = {
    loading: { progress: true },
    loadingAssignee: { progress: true },
    creating: null,
  };
  public hasManageTasksPermissions: boolean;

  private stateParams: StateParams;

  get formName(): AbstractControl {
    return this.form.get("name");
  }

  @Output() public readonly dismiss: EventEmitter<null> = new EventEmitter();

  constructor(
    private metricsFacade: MetricsFacade,
    private goalsFacade: GoalsFacade,
    private tasksFacade: TasksFacade,
    private destroy$: DestroyService,
    private formBuilder: FormBuilder,
    private analyticsService: AnalyticsService,
    private broadcastService: BroadcastService,
    private stateService: StateService,
    routerGlobals: UIRouterGlobals
  ) {
    this.stateParams = routerGlobals.params;
  }

  public ngOnInit(): void {
    this.hasManageTasksPermissions = hasPermissions(reduxStoreContainer.reduxStore.getState(), "ManageTasks");

    const reduxStoreObserver = new ReduxStoreObserver(reduxStoreContainer.reduxStore);
    reduxStoreObserver
      .whenFetched$<ISessionsStoreState>("sessions")
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ sessions }) => {
        this.allOpenSessionsIds = filterOpenedActiveAndFutureSessions(sessions.items).map((session: Session) => session.id);
        this.setNewTaskInitialDetails();
        this.buildForm();

        if (this.task.parentId) {
          this.getParent(this.task.parentId, this.task.parentType as ParentOkrSelectorType);
          return;
        }

        delete this.indicators.loading;
      });
  }

  public updateCustomFields(customFields: CfMap): void {
    this.task.customFields = customFields;
  }

  public onKeydown(event: KeyboardEvent, field: keyof FieldsToPreventDefault): void {
    const isEnterKey = event.key === "Enter";

    if (!isEnterKey || !this.fieldsToPreventDefault[field]) return;

    event.preventDefault(); // prevents opening the Cf Drop-down menu when hitting Enter
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      return this.form.markAllAsTouched();
    }

    for (const [key, value] of Object.entries(this.form.value)) {
      this.task[key] = value;
    }

    this.createTask();
  }

  public updateTaskOwner(ownerIds: string[]): void {
    const ownerId = ownerIds[0] || this.assignCurrentUserAsInitialOwner();

    this.task.ownerId = ownerId;
  }

  public updateTaskParent(parent: IParentSelector): void {
    this.task.parentId = parent.id;
    this.task.parentType = parent.type;
  }

  private createTask(): void {
    this.indicators.creating = { progress: true };

    this.tasksFacade.create$(this.task).subscribe({
      next: (newTask: Task) => {
        this.trackRecommendationHelpfulness(newTask);

        this.stateService.go("^.^", undefined, { location: "replace" }).then(() => {
          this.stateService.go("._task.task", { taskId: newTask.id }, { location: "replace" });
          delete this.indicators.creating;
        });

        this.broadcastService.emit("taskCreated", { task: newTask, sessionId: this.parent ? this.parent.sessionId : null });
      },
      error: (error) => (this.indicators.creating = { error }),
    });
  }

  private trackRecommendationHelpfulness(task: Task): void {
    // recommendation is helpful if user creates task and does not remove himself as an owner
    this.analyticsService.track(recommendation, { is_useful: task.ownerId === getCurrentUserId(), flavor: Flavor.SMART_DEFAULT, action: "creates_task;owner" });
  }

  private getParentId(): string {
    if (this.stateParams.parentType === ContributorTypeEnum.metric) {
      return this.stateParams.metricId;
    }

    // defaults to parent type 'goal'
    return this.stateParams.parentId || this.stateParams.id || "";
  }

  private getParent(parentId: string, parentType: ParentOkrSelectorType): Subscription {
    const getParentObservable: Observable<TaskParentItem> =
      parentType === ContributorTypeEnum.metric
        ? this.metricsFacade.getMetric$(parentId)
        : this.goalsFacade.getAll$({ filter: { _id: parentId }, fields: ["name", "sessionId"] });

    return getParentObservable.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (parent: TaskParentItem) => {
        this.parent = { ...parent, type: parentType };
        delete this.indicators.loading;
      },
      error: (error) => (this.indicators.loading = { error }),
    });
  }

  private assignCurrentUserAsInitialOwner(): string {
    const currentUserId = getCurrentUserId();
    delete this.indicators.loadingAssignee;
    return currentUserId;
  }

  private setNewTaskInitialDetails(): void {
    this.task = <Task>{
      name: "",
      description: "",
      ownerId: this.assignCurrentUserAsInitialOwner(),
      status: this.statuses.toDo,
      parentId: this.getParentId(),
      parentType: this.stateParams.parentType,
    };
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      name: new FormControl(this.task.name, [Validators.required]),
      status: new FormControl(this.task.status, [Validators.required]),
      dueDate: new FormControl(null),
    });
  }
}
